import AuthService from "../services/AuthService";
import utils from "./utils";
import logger from "@/utils/logger";

const REQUEST_TIMEOUT_MS = 120000;
const MAX_JSON_RESPONSE_BYTES = 25 * 1024 * 1024;

async function fetchWithGuards(url, options, { checkJsonSize = false } = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    if (checkJsonSize) {
      const lenHeader = response.headers.get("content-length");
      const len = lenHeader ? parseInt(lenHeader, 10) : NaN;
      if (Number.isFinite(len) && len > MAX_JSON_RESPONSE_BYTES) {
        throw new Error(
          `Response too large (${Math.round(len / 1024 / 1024)} MB). Aborting to protect the browser. URL: ${url}`
        );
      }
    }
    return response;
  } catch (err) {
    if (err.name === "AbortError") {
      throw new Error(`Request timed out after ${REQUEST_TIMEOUT_MS / 1000}s: ${url}`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function readJsonCapped(response, maxBytes) {
  if (!response.body || typeof response.body.getReader !== "function") {
    return response.json();
  }
  const reader = response.body.getReader();
  const chunks = [];
  let received = 0;
  let done = false;
  while (!done) {
    const result = await reader.read();
    done = result.done;
    if (done) break;
    received += result.value.length;
    if (received > maxBytes) {
      try { await reader.cancel(); } catch (_) { /* noop */ }
      throw new Error(
        `Response exceeded ${Math.round(maxBytes / 1024 / 1024)} MB. Aborting to protect the browser.`
      );
    }
    chunks.push(result.value);
  }
  const merged = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }
  return JSON.parse(new TextDecoder("utf-8").decode(merged));
}

export default {

  async put(url, data) {
    var token = null;
    try{
      token = window.App.$store.getters["main/getUser"]().access_token;

      if(!token){      
        throw new Error();
      }      
    }
    catch{
      var auth = await AuthService.create();
      token = await auth.getAccessToken();      
    }

    if (url.indexOf("http://") < 0 && url.indexOf("https://") < 0) {
      var baseUrl = (await utils.GetConfig()).API_BASE_URL;
      url = baseUrl + url;
    }

    try {
      const response = await fetchWithGuards(url, {
        method: "PUT",
        headers: {
          "Content-type": "application/json; charset=utf-8",
          Authorization: `Bearer ${token}`,
        },
        mode: "cors",
        body: data ? JSON.stringify(data) : null,
        credentials: "same-origin",
        cache: "no-cache",
      }, { checkJsonSize: true });

      if (response.ok) {
        if (response.redirected === true) {
          window.top.location.href = response.url.split(/[?#]/)[0];
        }
        return await response.text();
      } else {
        var t = await response.text();
        throw `${response.status} ${response.statusText}: ${t}`;
      }
    } catch (error) {
      logger.log(error);
      throw error;
    }
  },

  async post(url, data) {
    var token = null;
    try{
      token = window.App.$store.getters["main/getUser"]().access_token;

      if(!token){      
        throw new Error();
      }      
    }
    catch{
      var auth = await AuthService.create();
      token = await auth.getAccessToken();      
    }

    if (url.indexOf("http://") < 0 && url.indexOf("https://") < 0) {
      var baseUrl = (await utils.GetConfig()).API_BASE_URL;
      url = baseUrl + url;
    }

    try {
      const response = await fetchWithGuards(url, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=utf-8",
          Authorization: `Bearer ${token}`,
        },
        mode: "cors",
        body: data ? JSON.stringify(data) : null,
        credentials: "same-origin",
        cache: "no-cache",
      }, { checkJsonSize: true });

      if (response.ok) {
        if (response.redirected === true) {
          window.top.location.href = response.url.split(/[?#]/)[0];
        }
        return await response.text();
      } else {
        var t = await response.text();
        throw `${response.status} ${response.statusText}: ${t}`;
      }
    } catch (error) {
      logger.log(error);
      throw error;
    }
  },

  async delete(url) {
    var token = null;
    try{
      token = window.App.$store.getters["main/getUser"]().access_token;

      if(!token){      
        throw new Error();
      }      
    }
    catch{
      var auth = await AuthService.create();
      token = await auth.getAccessToken();      
    }

    if (url.indexOf("http://") < 0 && url.indexOf("https://") < 0) {
      var baseUrl = (await utils.GetConfig()).API_BASE_URL;
      url = baseUrl + url;
    }

    try {
      const response = await fetchWithGuards(url, {
        method: "DELETE",
        headers: {
          "Content-type": "application/json; charset=utf-8",
          Authorization: `Bearer ${token}`,
        },
        mode: "cors",
        credentials: "same-origin",
        cache: "no-cache",
      }, { checkJsonSize: true });

      if (response.ok) {
        if (response.redirected === true) {
          window.top.location.href = response.url.split(/[?#]/)[0];
        }
        return await response.text();
      } else {
        var t = await response.text();
        throw `${response.status} ${response.statusText}: ${t}`;
      }
    } catch (error) {
      logger.log(error);
      throw error;
    }
  },

  async getRaw(url) {
    var token = null;
    try{
      token = window.App.$store.getters["main/getUser"]().access_token;

      if(!token){      
        throw new Error();
      }      
    }
    catch{
      var auth = await AuthService.create();
      token = await auth.getAccessToken();      
    }

    if (url.indexOf("http://") < 0 && url.indexOf("https://") < 0) {
      var baseUrl = (await utils.GetConfig()).API_BASE_URL;
      url = baseUrl + url;
    }

    return fetchWithGuards(url, {
      cache: "no-cache",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw `${response.status} ${response.statusText}`;
        }
        return response.text();
      })
      .catch((error) => {
        logger.log("Error: " + error);
        throw error;
      });
  },

  async get(url) {  
    var token = null;
    try{
      token = window.App.$store.getters["main/getUser"]().access_token;

      if(!token){      
        throw new Error();
      }      
    }
    catch{
      var auth = await AuthService.create();
      token = await auth.getAccessToken();      
    }

    if (url.indexOf("http://") < 0 && url.indexOf("https://") < 0) {
      var baseUrl = (await utils.GetConfig()).API_BASE_URL;
      url = baseUrl + url;
    }
    try{
      var response = await fetchWithGuards(url, {
        cache: "no-cache",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }, { checkJsonSize: true });

      if (!response.ok) {
        if(response.status == 400)
        {
          throw await response.text();
        }
        else{
          throw `${response.status} ${response.statusText}`;
        }          
      }
      return readJsonCapped(response, MAX_JSON_RESPONSE_BYTES);

    }catch(error) {
        logger.log("Error: " + error);
        throw error;
    }
  },

  async getFile(url) {
    var token = null;
    try{
      token = window.App.$store.getters["main/getUser"]().access_token;

      if(!token){      
        throw new Error();
      }      
    }
    catch{
      var auth = await AuthService.create();
      token = await auth.getAccessToken();      
    }

    if (url.indexOf("http://") < 0 && url.indexOf("https://") < 0) {
      var baseUrl = (await utils.GetConfig()).API_BASE_URL;
      url = baseUrl + url;
    }

    return fetchWithGuards(url, {
      cache: "no-cache",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw `${response.status} ${response.statusText}`;
        }
        return response.arrayBuffer();
      })
      .catch((error) => {
        logger.log("Error: " + error);
        throw error;
      });
  },

  async postJson(url, data) {
    var token = null;
    try{
      token = window.App.$store.getters["main/getUser"]().access_token;

      if(!token){      
        throw new Error();
      }      
    }
    catch{
      var auth = await AuthService.create();
      token = await auth.getAccessToken();      
    }

    if (url.indexOf("http://") < 0 && url.indexOf("https://") < 0) {
      var baseUrl = (await utils.GetConfig()).API_BASE_URL;
      url = baseUrl + url;
    }
    try {
      const response = await fetchWithGuards(url, {
        method: "post",
        headers: {
          "Content-type": "application/json; charset=utf-8",
          Authorization: `Bearer ${token}`,
        },
        mode: "same-origin",
        body: data ? JSON.stringify(data) : null,
        credentials: "include",
      }, { checkJsonSize: true });

      if (response.ok) {
        // 401 causes redirect
        if (response.redirected === true) {
          window.top.location.href = response.url.split(/[?#]/)[0];
        } else {
          return await readJsonCapped(response, MAX_JSON_RESPONSE_BYTES);
        }
      } else {
        throw `${response.status} ${response.statusText}`;
      }
    } catch (error) {
      logger.log(error);
      throw error;
    }
  },

  async postFormData(url, data) {
    var token = null;
    try{
      token = window.App.$store.getters["main/getUser"]().access_token;

      if(!token){      
        throw new Error();
      }      
    }
    catch{
      var auth = await AuthService.create();
      token = await auth.getAccessToken();      
    }

    if (url.indexOf("http://") < 0 && url.indexOf("https://") < 0) {
      var baseUrl = (await utils.GetConfig()).API_BASE_URL;
      url = baseUrl + url;
    }
    try {
      const response = await fetchWithGuards(url, {
        method: "post",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        mode: "cors",
        body: data,
        credentials: "same-origin",
        cache: "no-cache",
      }, { checkJsonSize: true });

      if (response.ok) {
        // 401 causes redirect
        if (response.redirected === true) {
          window.top.location.href = response.url.split(/[?#]/)[0];
        } else {
          return await readJsonCapped(response, MAX_JSON_RESPONSE_BYTES);
        }
      } else {
        var t = await response.text();
        throw `${response.status} ${response.statusText}: ${t}`;
      }
    } catch (error) {
      logger.log(error);
      throw error;
    }
  },

};
